// src\components\Forms\GenerateBillsModal.js

import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, InputGroup, Alert, Row, Col } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { formatDate, formatCurrency } from '../../utils/formatters';
import { getRequirement, generateBill } from '../../services/RequirementService';
import { getBankAccounts } from '../../services/BankAccountService';
import { sanitizeInput } from '../../utils/sanitize';

const GenerateBillsModal = ({ show, onHide, requirementId, onSuccess }) => {
  const [requirement, setRequirement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [numberOfBills, setNumberOfBills] = useState(1);
  const [customAmount, setCustomAmount] = useState('');
  const [useCustomAmount, setUseCustomAmount] = useState(false);
  const [skipNext, setSkipNext] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [useCustomStartDate, setUseCustomStartDate] = useState(false);
  const [applyToAllFuture, setApplyToAllFuture] = useState(false);
  const [includeNotes, setIncludeNotes] = useState(false);
  const [notes, setNotes] = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState('');

  // Reset state when modal is closed
  useEffect(() => {
    if (!show) {
      setIsExpanded(false);
      setNumberOfBills(1);
      setCustomAmount('');
      setUseCustomAmount(false);
      setSkipNext(false);
      setCustomStartDate(null);
      setUseCustomStartDate(false);
      setApplyToAllFuture(false);
      setIncludeNotes(false);
      setNotes('');
      setSelectedBankAccount('');
    }
  }, [show]);

  useEffect(() => {
    if (show && requirementId) {
      fetchRequirementDetails();
      fetchBankAccounts();
    }
  }, [show, requirementId]);

  const fetchRequirementDetails = async () => {
    setLoading(true);
    try {
      const data = await getRequirement(requirementId);
      setRequirement(data);
      setCustomAmount(data.amount);
      setError(null);
    } catch (err) {
      console.error('Error fetching requirement details:', err);
      setError('Failed to load requirement details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const accounts = await getBankAccounts();
      setBankAccounts(accounts);
      if (accounts.length > 0) {
        setSelectedBankAccount(accounts[0].id);
      }
    } catch (err) {
      console.error('Error fetching bank accounts:', err);
    }
  };

  const handleGenerateNextBill = async () => {
    try {
      if (!selectedBankAccount) {
        setError('Please select a bank account');
        return;
      }
      
      const result = await generateBill(requirementId, {
        bank_account: selectedBankAccount
      });
      onSuccess(result);
      onHide();
    } catch (err) {
      setError('Failed to generate bill. Please try again.');
    }
  };

  const handleAdvancedGenerate = async () => {
    try {
      if (!selectedBankAccount) {
        setError('Please select a bank account');
        return;
      }
      
      const result = await generateBill(requirementId, {
        count: numberOfBills,
        skip_next: skipNext,
        custom_amount: useCustomAmount ? customAmount : null,
        custom_start_date: useCustomStartDate ? customStartDate : null,
        apply_to_all_future: applyToAllFuture,
        notes: includeNotes ? notes : null,
        bank_account: selectedBankAccount
      });
      onSuccess(result);
      onHide();
    } catch (err) {
      setError('Failed to generate bills. Please try again.');
    }
  };

  const calculateNextDueDate = () => {
    if (!requirement) return null;
    const lastBill = requirement.last_generated_date;
    const nextDate = new Date(lastBill || requirement.next_due_date);
    
    switch (requirement.frequency) {
      case 'weekly':
        nextDate.setDate(nextDate.getDate() + 7);
        break;
      case 'fortnightly':
        nextDate.setDate(nextDate.getDate() + 14);
        break;
      case 'monthly':
        nextDate.setMonth(nextDate.getMonth() + 1);
        break;
      case 'quarterly':
        nextDate.setMonth(nextDate.getMonth() + 3);
        break;
      case 'yearly':
        nextDate.setFullYear(nextDate.getFullYear() + 1);
        break;
      default:
        return null;
    }
    return nextDate;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Generate Bills</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        {loading ? (
          <p>Loading requirement details...</p>
        ) : requirement ? (
          <>
            <div className="mb-4">
              <h5>{requirement.name}</h5>
              <p className="text-muted">{requirement.description}</p>

              <Row className="mt-3">
                <Col md={6}>
                  <p><strong>Current Amount:</strong> {formatCurrency(requirement.amount)}</p>
                  <p><strong>Frequency:</strong> {requirement.frequency}</p>
                </Col>
                <Col md={6}>
                  {requirement.last_generated_date && (
                    <p><strong>Last Generated Bill Due:</strong> {formatDate(requirement.last_generated_date)}</p>
                  )}
                  <p><strong>Next Due Date:</strong> {formatDate(calculateNextDueDate())}</p>
                </Col>
              </Row>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>Bank Account</Form.Label>
              <Form.Select
                value={selectedBankAccount}
                onChange={(e) => setSelectedBankAccount(e.target.value)}
                required
              >
                <option value="">Select bank account</option>
                {bankAccounts.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name || account.nickname}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {!isExpanded ? (
              <div className="d-grid gap-2">
                <Button variant="primary" onClick={handleGenerateNextBill} disabled={!selectedBankAccount}>
                  Generate Next Bill
                </Button>
                <Button variant="outline-primary" onClick={() => setIsExpanded(true)}>
                  Advanced Generate
                </Button>
              </div>
            ) : (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Number of Bills to Generate</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    max="12"
                    value={numberOfBills}
                    onChange={(e) => setNumberOfBills(parseInt(e.target.value) || 1)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Skip next due date"
                    checked={skipNext}
                    onChange={(e) => setSkipNext(e.target.checked)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Use custom amount"
                    checked={useCustomAmount}
                    onChange={(e) => setUseCustomAmount(e.target.checked)}
                  />
                  {useCustomAmount && (
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      className="mt-2"
                    />
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Use custom start date"
                    checked={useCustomStartDate}
                    onChange={(e) => setUseCustomStartDate(e.target.checked)}
                  />
                  {useCustomStartDate && (
                    <DatePicker
                      selected={customStartDate}
                      onChange={(date) => setCustomStartDate(date)}
                      dateFormat="yyyy-MM-dd"
                      className="form-control mt-2"
                    />
                  )}
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Apply to all future bills"
                    checked={applyToAllFuture}
                    onChange={(e) => setApplyToAllFuture(e.target.checked)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    label="Add notes to generated bills"
                    checked={includeNotes}
                    onChange={(e) => setIncludeNotes(e.target.checked)}
                  />
                  {includeNotes && (
                    <Form.Control
                      as="textarea"
                      rows={2}
                      className="mt-2"
                      placeholder="Enter notes for the generated bills"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  )}
                </Form.Group>
              </>
            )}
          </>
        ) : (
          <p>No requirement found</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        {isExpanded && (
          <Button
            variant="primary"
            onClick={handleAdvancedGenerate}
            disabled={loading || !requirement || !selectedBankAccount}
          >
            Generate Bills
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default GenerateBillsModal;
